// Centralized language configuration (SPEED-1113).
// `nativeName` and `displayCode` are FIXED presentation values for the top-nav language
// selector: they are always shown in the language's own familiar form, never translated into
// the active interface language, so a user who cannot read the current language can still find
// and switch back to their own. `displayCode` is the user-facing code and is kept separate from
// the internal `id`/`htmlLang` locale identifiers where they differ (e.g. `BM` for Malay `ms`).
// `labelKey` stays for contexts that intentionally show the current language translated into the
// active locale (e.g. localization settings). `englishName` is a fixed search alias for the selector's
// search field (SPEED-1129), so "German" finds Deutsch whatever the interface language is.
// Adding a language is a single row here.
export const SUPPORTED_LANGUAGES = [
  { id: 'en', htmlLang: 'en', displayCode: 'EN', nativeName: 'English', englishName: 'English', labelKey: 'languages.en' },
  { id: 'ms', htmlLang: 'ms', displayCode: 'BM', nativeName: 'Bahasa Malaysia', englishName: 'Malay', labelKey: 'languages.ms' },
  { id: 'id', htmlLang: 'id', displayCode: 'ID', nativeName: 'Bahasa Indonesia', englishName: 'Indonesian', labelKey: 'languages.id' },
  { id: 'zh', htmlLang: 'zh-Hans', displayCode: 'ZH-CN', nativeName: '简体中文', englishName: 'Simplified Chinese', labelKey: 'languages.zh' },
  { id: 'fr', htmlLang: 'fr', displayCode: 'FR', nativeName: 'Français', englishName: 'French', labelKey: 'languages.fr' },
  // SPEED-1129. Traditional Chinese and Brazilian Portuguese are their own ids, never folded into zh / pt.
  { id: 'ja', htmlLang: 'ja', displayCode: 'JA', nativeName: '日本語', englishName: 'Japanese', labelKey: 'languages.ja' },
  { id: 'ko', htmlLang: 'ko', displayCode: 'KO', nativeName: '한국어', englishName: 'Korean', labelKey: 'languages.ko' },
  { id: 'th', htmlLang: 'th', displayCode: 'TH', nativeName: 'ภาษาไทย', englishName: 'Thai', labelKey: 'languages.th' },
  { id: 'vi', htmlLang: 'vi', displayCode: 'VI', nativeName: 'Tiếng Việt', englishName: 'Vietnamese', labelKey: 'languages.vi' },
  { id: 'fil', htmlLang: 'fil', displayCode: 'FIL', nativeName: 'Filipino', englishName: 'Filipino', labelKey: 'languages.fil' },
  { id: 'zh-TW', htmlLang: 'zh-Hant-TW', displayCode: 'ZH-TW', nativeName: '繁體中文', englishName: 'Traditional Chinese', labelKey: 'languages.zh-TW' },
  { id: 'es', htmlLang: 'es', displayCode: 'ES', nativeName: 'Español', englishName: 'Spanish', labelKey: 'languages.es' },
  { id: 'de', htmlLang: 'de', displayCode: 'DE', nativeName: 'Deutsch', englishName: 'German', labelKey: 'languages.de' },
  { id: 'pt-BR', htmlLang: 'pt-BR', displayCode: 'PT-BR', nativeName: 'Português (Brasil)', englishName: 'Portuguese (Brazil)', labelKey: 'languages.pt-BR' },
  { id: 'hi', htmlLang: 'hi', displayCode: 'HI', nativeName: 'हिन्दी', englishName: 'Hindi', labelKey: 'languages.hi' },
] as const;

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];
export type SupportedLanguageId = SupportedLanguage['id'];

export const SUPPORTED_LANGUAGE_IDS: readonly SupportedLanguageId[] =
  SUPPORTED_LANGUAGES.map((language) => language.id);

export function isSupportedLanguage(value: string | null | undefined): value is SupportedLanguageId {
  return !!value && SUPPORTED_LANGUAGE_IDS.includes(value as SupportedLanguageId);
}

export function supportedLanguage(value: string | null | undefined): SupportedLanguage {
  return SUPPORTED_LANGUAGES.find((language) => language.id === value) ?? SUPPORTED_LANGUAGES[0];
}

/** Lower-case, trimmed, without diacritics, so "francais" finds "Français" and "viet" finds "Tiếng Việt". */
function searchable(value: string): string {
  return value.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase().trim();
}

/**
 * SPEED-1129: the language selector's search. Matches the fixed native name, display code, locale id and English
 * name, none of which depend on the active interface language. Exact code matches come first, then names or codes
 * that start with the text, then any other match; ties keep the registry order. A blank query returns every language.
 */
export function searchLanguages(query: string | null | undefined): SupportedLanguageId[] {
  const text = searchable(query ?? '');
  if (!text) {
    return [...SUPPORTED_LANGUAGE_IDS];
  }
  const ranked: { id: SupportedLanguageId; rank: number }[] = [];
  for (const language of SUPPORTED_LANGUAGES) {
    const codes = [language.displayCode, language.id].map(searchable);
    const names = [language.nativeName, language.englishName].map(searchable);
    const words = names.flatMap((name) => name.split(/[\s()]+/).filter(Boolean));
    const rank = codes.includes(text) ? 0
      : [...codes, ...names, ...words].some((value) => value.startsWith(text)) ? 1
      : [...codes, ...names].some((value) => value.includes(text)) ? 2
      : -1;
    if (rank >= 0) {
      ranked.push({ id: language.id, rank });
    }
  }
  return ranked.sort((a, b) => a.rank - b.rank).map((entry) => entry.id);
}
